type PlayAudioParams = {
  audioFile: string
  speakerDeviceId?: string
  volume?: number
  loop?: boolean
  resetCurrentTime?: boolean
}

type StopAudioParams = {
  audioFile: string
  speakerDeviceId?: string
}

type AudioWithSinkId = HTMLAudioElement & {
  setSinkId?: (sinkId: string) => Promise<void>
  sinkId?: string
}

const audioCache = new Map<string, HTMLAudioElement>()

function getAudioCacheKey(audioFile: string, speakerDeviceId?: string): string {
  return `${audioFile}::${speakerDeviceId ?? 'default'}`
}

async function setAudioSink(audio: AudioWithSinkId, speakerDeviceId?: string): Promise<void> {
  if (!speakerDeviceId || typeof audio.setSinkId !== 'function') {
    return
  }

  if (audio.sinkId === speakerDeviceId) {
    return
  }

  try {
    await audio.setSinkId(speakerDeviceId)
  } catch (error) {
    console.warn('[audioPlayback] Could not set audio output device:', error)
  }
}

export async function playAudio({
  audioFile,
  speakerDeviceId,
  volume = 1.0,
  loop = false,
  resetCurrentTime = true
}: PlayAudioParams): Promise<HTMLAudioElement> {
  const cacheKey = getAudioCacheKey(audioFile, speakerDeviceId)
  let audio = audioCache.get(cacheKey)

  if (!audio) {
    audio = new Audio(audioFile)
    audioCache.set(cacheKey, audio)
  }

  audio.loop = loop
  audio.volume = volume

  if (resetCurrentTime) {
    audio.currentTime = 0
  }

  await setAudioSink(audio as AudioWithSinkId, speakerDeviceId)
  await audio.play()

  return audio
}

export function stopAudio({ audioFile, speakerDeviceId }: StopAudioParams): void {
  const cacheKey = getAudioCacheKey(audioFile, speakerDeviceId)
  const audio = audioCache.get(cacheKey)

  if (!audio) {
    return
  }

  audio.pause()
  audio.currentTime = 0
}
